import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

// Relative (no leading slash) so the browser resolves against the
// document URL — works locally (root = /), under `vite preview`, and
// when served from Clearly at /site/<uid>/<cid>/ where the absolute
// `/space-music.mp3` would 404 against the worker root. The HTML path
// rewriter only fixes `src=`/`href=` attributes, not JS-driven URLs.
const AUDIO_SRC = "space-music.mp3";
const MAX_GAIN = 0.32; // softer than before
const LOWPASS_CUTOFF_HZ = 1400; // warm/muffled ambient character

export default function AudioToggle() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const filterRef = useRef<BiquadFilterNode | null>(null);
  const graphBuiltRef = useRef(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const a = new Audio(AUDIO_SRC);
    a.loop = true;
    /* ⚠⚠ `none`, NOT `auto`. This was `auto`, which fetched 1.4 MB of audio on
       mount for every visitor, the overwhelming majority of whom never press
       the button. The file now downloads on the first press and not before.
       ⚠ The button is therefore enabled immediately rather than waiting on
       `canplaythrough` — with `preload="none"` that event does not fire until
       playback is attempted, so gating on it would leave the control
       permanently disabled. `play()` handles the load itself. */
    a.preload = "none";
    a.crossOrigin = "anonymous";
    audioRef.current = a;
    return () => {
      a.pause();
      audioRef.current = null;
      ctxRef.current?.close().catch(() => {});
      ctxRef.current = null;
      gainRef.current = null;
      filterRef.current = null;
      graphBuiltRef.current = false;
    };
  }, []);

  /* ⚠⚠ REMOVED: an effect that worked around the autoplay policy. It attempted
     playback 400ms after mount and, failing that, armed one-shot `pointerdown`
     and `keydown` listeners on the document so that the visitor's FIRST CLICK
     ANYWHERE started the music. On a page whose whole purpose is to be clicked
     and dragged, that meant background audio began the moment anyone touched
     the globe. `playing` initialising to `false` made it look opt-in in the
     state and it was not.

     Sound now starts on one thing only: pressing the button that says so.
     The autoplay policy is a protection for the visitor, and routing around it
     is a choice a reader of this file would judge — correctly. */

  // Build the Web Audio graph on first play (requires user gesture).
  const buildGraph = () => {
    if (graphBuiltRef.current) return;
    const a = audioRef.current;
    if (!a) return;
    const AC: typeof AudioContext =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    const ctx = new AC();
    const src = ctx.createMediaElementSource(a);
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = LOWPASS_CUTOFF_HZ;
    filter.Q.value = 0.7; // gentle resonance, no ringing
    const gain = ctx.createGain();
    gain.gain.value = 0;
    src.connect(filter).connect(gain).connect(ctx.destination);
    ctxRef.current = ctx;
    filterRef.current = filter;
    gainRef.current = gain;
    graphBuiltRef.current = true;
  };

  const fadeGain = (target: number, ms = 900) => {
    const ctx = ctxRef.current;
    const gain = gainRef.current;
    if (!ctx || !gain) return;
    const now = ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(target, now + ms / 1000);
  };

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      fadeGain(0, 650);
      setTimeout(() => a.pause(), 700);
      setPlaying(false);
    } else {
      try {
        buildGraph();
        const ctx = ctxRef.current;
        if (ctx?.state === "suspended") await ctx.resume();
        await a.play();
        fadeGain(MAX_GAIN, 1400);
        setPlaying(true);
      } catch {
        /* decode / autoplay failure — ignore */
      }
    }
  };

  return (
    <motion.button
      className={`audio-toggle ${playing ? "is-playing" : ""}`}
      onClick={toggle}
      aria-label={playing ? "Mute background music" : "Play background music"}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.25 }}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.96 }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 20 20"
        className="audio-icon"
        aria-hidden="true"
      >
        <path d="M3 7.5 V12.5 H6 L10.5 16 V4 L6 7.5 Z" fill="currentColor" />
        <g className="audio-waves">
          <path
            d="M13 7 Q 15 10 13 13"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            d="M15.5 5 Q 19 10 15.5 15"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </g>
        <line
          className="audio-mute-slash"
          x1="2"
          y1="2"
          x2="18"
          y2="18"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
      <span className="audio-label">{playing ? "music on" : "music"}</span>
    </motion.button>
  );
}
